import { BusinessVerifier } from '@models/business/BusinessVerifier'
import { businessVerifierSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createBusinessVerifier as createBusinessVerifierAPI,
  deleteBusinessVerifier as deleteBusinessVerifierAPI,
  fetchBusinessVerifierById,
  fetchBusinessVerifiers,
  updateBusinessVerifier as updateBusinessVerifierAPI,
} from '@services/business/BusinessVerifierService'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<BusinessVerifier> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'businessVerifier',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getBusinessVerifiersSuccess,
  setQuery: setBusinessVerifierQuery,
  reset: resetBusinessVerifierState,
  resetQueryAndIds: resetBusinessVerifierQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: BusinessVerifier }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectBusinessVerifiers = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedBusinessVerifier = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectBusinessVerifierById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectBusinessVerifiersByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedBusinessVerifier =
  (BusinessVerifier: BusinessVerifier | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<BusinessVerifier>(BusinessVerifier)
      )
    )

export const getBusinessVerifiers = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchBusinessVerifiers(params),
    [businessVerifierSchema],
    slice.actions
  )

export const createBusinessVerifier = (BusinessVerifier: BusinessVerifier) =>
  SBAPI.CreateDispatched(
    createBusinessVerifierAPI(BusinessVerifier),
    businessVerifierSchema,
    slice.actions
  )

export const getBusinessVerifierWithId = (id: number) =>
  SBAPI.FetchDispatched<BusinessVerifier>(
    fetchBusinessVerifierById(id),
    businessVerifierSchema,
    slice.actions
  )

export const updateBusinessVerifier = (BusinessVerifier: BusinessVerifier) =>
  SBAPI.UpdateDispatched<BusinessVerifier>(
    updateBusinessVerifierAPI(BusinessVerifier),
    BusinessVerifier,
    slice.actions
  )

export const deleteBusinessVerifier = (BusinessVerifier: BusinessVerifier) =>
  SBAPI.DestroyDispatched<BusinessVerifier>(
    deleteBusinessVerifierAPI(BusinessVerifier),
    BusinessVerifier,
    slice.actions
  )
